import type { Root } from 'react-dom/client'
import type { ButtonConfig } from '../../declare'
import { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react'
import { createRoot } from 'react-dom/client'
import { autorun, toJS } from 'mobx'
import { Dropdown, Table } from 'antd'
import { MoreOutlined } from '@ant-design/icons'
import { ListPropsContext, postMsg } from 'src/components'
import { searchStore, listStore } from 'src/stores'
import { getTableData } from 'src/components/root/tabler'
import { tableExportToBook } from 'src/utils/xlsx'
import { $consts } from 'src/plugins'
import { debounce } from 'lodash'

type Props = {
  config: ButtonConfig
}
const TableExport = (props: Props) => {
  const listProps = useContext(ListPropsContext)
  const search = searchStore.search.get(listProps.formCode)
  const table = listStore.table.get(listProps.formCode)

  const roots = useRef<Root[]>([])
  useEffect(() => {
    return () => {
      roots.current.forEach(root => root.unmount())
    }
  }, [])

  // 导出文件名
  const fileName = useRef('基础表格')
  useEffect(() => autorun(() => {
    fileName.current = table?.tableConfig?.Nav_Form?.NAME || '基础表格'
  }), [])

  // 实际导出表格的 dataSource
  const exportData = useRef<any[]>([])

  // 实际导出表格的 columns
  const virtualColumns = useRef<any[]>([])
  useEffect(() => autorun(() => {
    virtualColumns.current = toJS(table?.tableData?.columns || []).filter((item: any) => {
      // 过滤掉序号列、操作列
      return item.key !== $consts['TABLE/LIST_TABLE_COLUMN_NO'] && item.key !== $consts['TABLE/LIST_TABLE_COLUMN_OPERATE']
    })
  }), [])

  // dom 节点导出成 excel 表格
  // debounce 是因为 table ref 会被执行多次
  const domsExport = useCallback(debounce((tableDom: Element) => {
    postMsg('请稍后...', 'info')
    const presetDoms: Element[] = []
    const doms = presetDoms.concat(tableDom)
    tableExportToBook(doms, fileName.current, params => {
      // 第一行是表头，所以 -1
      const record = exportData.current[params.row - 1]
      const field = virtualColumns.current[params.col]?.dataIndex
      return record?.[field] !== undefined && (typeof record[field] !== 'number')
    })
  }, 200), [])

  // 构建一个真实 table dom 并导出
  const handleExport = () => {
    const wrapperEle: any = document.createElement('div')
    const root = createRoot(wrapperEle)
    root.render(
      <Table
        ref={ref => ref && domsExport(wrapperEle)}
        rowKey='ID'
        columns={virtualColumns.current}
        dataSource={exportData.current}
        pagination={false}
      />
    )
    roots.current.push(root)
  }

  // 按查询条件导出
  const handleOnlineExport = (pageOption: any = { PageIndex: 1, Limit: 1000 }) => {
    getTableData({
      tableConfig: table?.tableConfig,
      pageOption: pageOption,
      // ListSearch 的搜索参数
      searchFilterGroup: search?.getSearchFilterGroup(),
      // 额外请求参数
      filterGroup: listProps?.formData?.filterGroup
    }).then((data: any) => {
      const { dataSource, total } = data
      exportData.current = exportData.current.concat(dataSource)
      const { PageIndex, Limit } = pageOption
      // 为保证服务器效率，分页加载
      if (PageIndex * Limit < total) {
        pageOption = { PageIndex: PageIndex + 1, Limit }
        handleOnlineExport(pageOption)
      } else {
        handleExport()
      }
    })
  }

  const handleMenuClick = ({ key }: any) => {
    switch (key) {
      // 当前页导出
      case 'table':
        exportData.current = table?.tableData?.dataSource ?? []
        handleExport()
        break
      // 选中项导出
      case 'selected':
        exportData.current = table?.selectedData ?? []
        handleExport()
        break
      // 按查询条件导出
      case 'online':
        exportData.current = []
        handleOnlineExport()
        break
      default:
        return
    }
  }

  const [disabled, setDisabled] = useState(false)
  useEffect(() => autorun(() => {
    setDisabled(!table?.selectedData?.length)
  }), [])

  const overlay = useMemo(() => {
    return {
      items: [
        { key: 'selected', label: '按选择导出', disabled },
        { key: 'table', label: '导出当前页' },
      ],
      onClick: handleMenuClick
    }
  }, [disabled])

  return (
    <Dropdown.Button
      icon={<MoreOutlined />}
      onClick={() => handleMenuClick({ key: 'online' })}
      menu={overlay}
    >导出</Dropdown.Button>
  )
}

export default TableExport